<template>
  <div>
    <page-header
      :title="title"
      :breadcrumb="breadcrumb"
      :container-class="containerClass"
      class="mb-16pt"
      button-label="Email Logs"
      button-variant="outline-primary"
      :button-path="{ name: 'email-config-logs' }"
      info-text="Manage the automated course emails sent to your students."
    />

    <div class="page-section">
      <div :class="containerClass">
        <div class="card mb-0">
          <div class="card-header">
            <form class="search-form search-form--light d-lg-inline-flex mb-8pt mb-lg-0" @submit.prevent="">
              <b-form-input class="w-lg-auto" placeholder="Search by title" v-model="searchTerm" @input="onSearch" />
              <b-btn variant="flush" type="submit">
                <md-icon>search</md-icon>
              </b-btn>
            </form>
          </div>

          <b-table
            :fields="tableColumns"
            :items="emails"
            :busy="isLoading"
            head-variant="light"
            class="table-nowrap"
            hover
            responsive
            no-border-collapse
            no-local-sorting
            @row-clicked="onEdit"
          >
            <template #table-busy>
              <div class="text-center my-2">
                <b-spinner class="align-middle mr-2"></b-spinner>
                <strong>Loading...</strong>
              </div>
            </template>

            <template #cell(title)="data">
              <strong>{{ data.item.title }}</strong>
            </template>

            <template #cell(enabled)="data">
              <span class="badge" :class="data.item.enabled ? 'badge-success' : 'badge-secondary'">
                {{ data.item.enabled ? 'Enabled' : 'Disabled' }}
              </span>
            </template>

            <template #cell(actions)="data">
              <b-dropdown variant="link" toggle-class="text-decoration-none p-0" no-caret>
                <template #button-content>
                  <i class="material-icons icon-24pt text-info">more_vert</i>
                </template>
                <b-dropdown-item :to="{ name: 'edit-email-config', params: { id: data.item.id } }">
                  <md-icon class="text-info mr-2">edit</md-icon>
                  {{ $t('generalMsgs.edit') }}
                </b-dropdown-item>
              </b-dropdown>
            </template>

            <template #empty>
              <p class="text-center text-muted my-3">No emails found.</p>
            </template>
          </b-table>

          <div class="card-footer d-flex justify-content-end" v-if="totalEmails > perPage">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalEmails"
              :per-page="perPage"
              @change="onPageChange"
              class="mb-0"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Page from '@/components/Page.vue';
import PageHeader from '@/components/Ui/PageHeader.vue';
import { mapActions } from 'vuex';
import { debounce } from 'lodash';

export default {
  name: 'ListEmailConfigs',
  extends: Page,
  components: { PageHeader },

  data() {
    return {
      title: 'Email Configuration',
      emails: [],
      isLoading: false,
      searchTerm: '',
      currentPage: 1,
      perPage: 15,
      totalEmails: 0,
      tableColumns: [
        { key: 'actions', label: '', class: 'text-left', thStyle: { width: '48px' } },
        { key: 'title', label: 'Email' },
        { key: 'enabled', label: 'Status' }
      ]
    };
  },

  computed: {
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Email Configuration', active: true }
      ];
    }
  },

  methods: {
    ...mapActions('emailConfig', ['getAllEmails']),

    async fetchEmails() {
      this.isLoading = true;
      try {
        const params = {
          limit: this.perPage,
          offset: (this.currentPage - 1) * this.perPage,
          ...(this.searchTerm && { search: this.searchTerm })
        };
        const res = await this.getAllEmails(params);
        const payload = res.data || {};
        this.emails = payload.results || [];
        this.totalEmails = payload.count || this.emails.length;
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    onSearch: debounce(function() {
      this.currentPage = 1;
      this.fetchEmails();
    }, 400),

    onPageChange(page) {
      this.currentPage = page;
      this.$nextTick(this.fetchEmails);
    },

    onEdit(item) {
      this.$router.push({ name: 'edit-email-config', params: { id: item.id } });
    }
  },

  mounted() {
    this.fetchEmails();
  }
};
</script>
